import type { CSSProperties } from 'react';
import { LogoPlate } from '@/components/logo-plate';
import { copy, type CoinItem } from '@/content/copy';
import { logoMonogram, logoPath } from '@/content/payments';
import { cn } from '@/lib/cn';

const { coins } = copy;

/** Exposes the item index to the stylesheet, which spaces the orbit and times the coin faces. */
const indexVar = (index: number) => ({ '--i': index }) as CSSProperties;

/**
 * One chip on the orbit: the rail's logo plate(s) beside the ticker, coin name and rail. It
 * lights up while its coin is on the disc (`chip-glow`, timed like the faces by `--i`) and
 * counter-turns against the orbit so it stays upright; both in CSS (`.coin-chip`).
 */
function CoinChip({ coin, index }: { coin: CoinItem; index: number }) {
  return (
    <li style={indexVar(index)}>
      <span className="coin-chip">
        <span aria-hidden className="coin-chip-plates" data-count={coin.logos.length}>
          {coin.logos.map((logo) => (
            <LogoPlate
              key={logo}
              src={logoPath(logo)}
              monogram={logoMonogram(logo)}
              className="coin-chip-plate"
            />
          ))}
        </span>
        <span className="coin-chip-text">
          <span className="coin-chip-ticker">{coin.ticker}</span>
          <span className="coin-chip-name">
            {coin.name}
            <span className="sr-only">, {coin.currency}.</span>
          </span>
          <span className="coin-chip-rail">{coin.rail}</span>
        </span>
      </span>
    </li>
  );
}

/**
 * The media of the coins stage: one coin, face-on, turning through the six local currencies
 * on a blue pool with hairline rings. Built to be seen through the resting disc first and
 * then full bleed behind the section's copy, so the interesting part sits at the centre and
 * the edges are atmosphere. Once the frame opens (`--expand`, written by ScrollExpand) the
 * coin softens so it does not sit sharp under the copy. The turning disc is decoration.
 *
 * `CoinOrbit` is its companion in the stage's unclipped ornament layer, and both centre on
 * the stage, so the orbit rings the disc with its chips lapping the disc's edge. Sizes and
 * timings are the `.coin-figure` tokens on the ScrollExpand root. All motion is CSS
 * (`.coin-cycle`, `.coin-orbit`, `.coin-chip` in globals.css) and stops under reduced
 * motion, where the first coin and its chip stay lit.
 */
export function CoinStage({ className }: { className?: string }) {
  return (
    <div className={cn('coin-stage absolute inset-0 grid place-items-center', className)}>
      <div aria-hidden className="coin-stage-rings pointer-events-none absolute inset-0" />

      <div aria-hidden className="coin-cycle relative">
        {coins.items.map((coin, index) => (
          <span
            key={coin.ticker}
            style={indexVar(index)}
            className="coin-face absolute inset-0 text-[clamp(1.5rem,5vmin,2.6rem)] tracking-[-0.03em]"
          >
            {coin.symbol}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * The slow orbit of the six instant-payment rails the coin settles against, riding outside
 * the resting disc. It is also the page's list of coins: each chip reads ticker, coin name
 * and rail, with the currency name for assistive tech. As the frame opens the orbit widens,
 * fades and blurs away so nothing sits under the copy.
 */
export function CoinOrbit({ className }: { className?: string }) {
  return (
    <div className={cn('absolute inset-0 grid place-items-center', className)}>
      <ul className="coin-orbit m-0 list-none p-0" aria-label={coins.stage.label}>
        {coins.items.map((coin, index) => (
          <CoinChip key={coin.ticker} coin={coin} index={index} />
        ))}
      </ul>
    </div>
  );
}
